import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

const fa = n => Number(n || 0).toLocaleString('fa-IR');

export default function OrderFormModal({ product, open, onClose, variantSel }) {
  const navigate = useNavigate();
  const [customizations, setCustomizations] = useState({});
  const [busy, setBusy] = useState(false);
  const [livePrice, setLivePrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const fields = product?.customization_data?.fields || [];
  const hasFormula = !!(product?.customization_data?.pricing_formula);
  // فیلدهای فرم متصل (فرم‌ساز) — با پیشوند frm_ در customizations ذخیره می‌شوند
  const formFields = (product?.order_form?.fields || []).filter(f => f && f.id && f.type).map(f => ({
    name: 'frm_' + f.id, label: f.label || f.id, type: f.type, options: f.options || [], required: !!f.required,
  }));
  const allFields = [...fields, ...formFields];

  useEffect(() => {
    if (open && product && allFields.length) {
      const init = {};
      allFields.forEach(f => { init[f.name] = f.defaultValue || ''; });
      setCustomizations(init);
      setLivePrice(null);
      setBusy(false);
    }
  }, [open, product]);

  const handleInput = (name, val) => setCustomizations(prev => ({ ...prev, [name]: val }));

  // Debounced live price computation
  const computePrice = useCallback(async (cust) => {
    if (!open || !product || !hasFormula) return;
    const filled = fields.every(f => String(cust[f.name] ?? '').trim() !== '');
    if (!filled) { setLivePrice(null); return; }
    setPriceLoading(true);
    try {
      const r = await axios.post(`/products/${product.id}/price`, { customizations: cust });
      setLivePrice(r.data?.price ?? null);
    } catch { setLivePrice(null); }
    setPriceLoading(false);
  }, [open, product, hasFormula, fields]);

  useEffect(() => {
    if (!open || !hasFormula) return;
    const t = setTimeout(() => computePrice(customizations), 350);
    return () => clearTimeout(t);
  }, [customizations, open, hasFormula, computePrice]);

  const submitCart = async () => {
    if (!localStorage.getItem('customer_token')) { toast.error('لطفاً ابتدا وارد شوید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); return; }
    const needV = ((product && product.variants) || []).length > 0;
    if (needV && !(variantSel && Object.keys(variantSel).length)) { toast.error('ترکیب (رنگ/سایز) را انتخاب کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); return; }
    const missing = fields.find(f => !(customizations[f.name] || '').trim());
    if (missing) { toast.error(`ویژگی «${missing.name}» را انتخاب کنید.`, { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); return; }
    const missingForm = formFields.find(f => f.required && !(customizations[f.name] || '').trim());
    if (missingForm) { toast.error(`فیلد «${missingForm.label}» الزامی است.`, { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); return; }
    setBusy(true);
    try {
      await axios.post('/cart/add', {
        product_id: product.id,
        qty: Math.max(product.min_purchase || 1, 1),
        customizations,
        attributes: (variantSel && Object.keys(variantSel).length) ? { _variant: variantSel } : {},
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('customer_token') || ''}` } });
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('محصول به سبد خرید اضافه شد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'خطا در افزودن به سبد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    } finally {
      setBusy(false);
    }
  };

  if (!open || !allFields.length) return null;

  return (
    <>
      <div onClick={onClose} className="sheet-overlay" style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(12,10,9,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, direction: 'rtl', boxSizing: 'border-box' }} />
      <div className="sheet-card sheet-centered" style={{
        position: 'fixed', zIndex: 2001, top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 'min(520px, calc(100vw - 32px))', maxHeight: '90vh', overflowY: 'auto',
        background: 'var(--card, #fff)', borderRadius: 'var(--radius, 20px)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        padding: 0, direction: 'rtl', fontFamily: 'inherit',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid var(--border, #E4E4E7)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: 'var(--card, #fff)', zIndex: 2, borderRadius: 'var(--radius, 20px) var(--radius, 20px) 0 0',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--fg, #0C0A09)', fontFamily: 'var(--font-sans, inherit)' }}>
              {product?.name}
            </h3>
            <span style={{ fontSize: '0.78rem', color: 'var(--muted-fg, #475569)' }}>انتخاب ویژگی‌های سفارشی</span>
          </div>
          <button onClick={onClose} aria-label="بستن" style={{
            width: 34, height: 34, borderRadius: '50%', border: '1px solid var(--border, #D6D3D1)',
            background: 'var(--card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <span style={{ display: 'inline-flex' }}><X size={17} /></span>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {allFields.map(f => (
            <div key={f.name} style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 6, color: 'var(--fg, #0C0A09)' }}>
                {f.label || f.name}{f.required ? ' *' : ''}
              </label>
              {f.type === 'select' || f.type === 'radio' ? (
                <select
                  value={customizations[f.name] || ''}
                  onChange={e => handleInput(f.name, e.target.value)}
                  style={{
                    width: '100%', padding: '11px 14px', borderRadius: 12,
                    border: '1.5px solid var(--border, #D6D3D1)', fontFamily: 'inherit',
                    fontSize: '0.9rem', background: 'var(--card, #fff)', color: 'var(--fg, #0C0A09)',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                >
                  <option value="">انتخاب کنید</option>
                  {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'textarea' ? (
                <textarea
                  value={customizations[f.name] || ''}
                  onChange={e => handleInput(f.name, e.target.value)}
                  rows={3}
                  style={{
                    width: '100%', padding: '11px 14px', borderRadius: 12,
                    border: '1.5px solid var(--border, #D6D3D1)', fontFamily: 'inherit',
                    fontSize: '0.9rem', background: 'var(--card, #fff)', color: 'var(--fg, #0C0A09)',
                    outline: 'none', boxSizing: 'border-box', resize: 'vertical',
                  }}
                  placeholder={f.name}
                />
              ) : (
                <input
                  type={['number', 'phone', 'email', 'date'].includes(f.type) ? f.type : 'text'}
                  value={customizations[f.name] || ''}
                  onChange={e => handleInput(f.name, e.target.value)}
                  style={{
                    width: '100%', padding: '11px 14px', borderRadius: 12,
                    border: '1.5px solid var(--border, #D6D3D1)', fontFamily: 'inherit',
                    fontSize: '0.9rem', background: 'var(--card, #fff)', color: 'var(--fg, #0C0A09)',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                  placeholder={f.name}
                />
              )}
            </div>
          ))}

          {/* Live price box */}
          {hasFormula && (
          <div style={{
            marginTop: 4, marginBottom: 16,
            background: 'var(--muted, #F5F5F4)', borderRadius: 14,
            padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--muted-fg, #475569)', fontWeight: 600 }}>
              قیمت محاسبه‌شده:
            </span>
            {priceLoading ? (
              <span style={{ fontSize: '0.9rem', color: 'var(--muted-fg, #475569)' }}>در حال محاسبه...</span>
            ) : livePrice != null ? (
              <b style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--accent, #A16207)' }}>
                {fa(livePrice)} تومان
              </b>
            ) : (
              <span style={{ fontSize: '0.82rem', color: '#A1A1AA' }}>ویژگی‌ها را کامل انتخاب کنید</span>
            )}
          </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px 20px', borderTop: '1px solid var(--border, #E4E4E7)',
          position: 'sticky', bottom: 0, background: 'var(--card, #fff)', borderRadius: '0 0 var(--radius, 20px) var(--radius, 20px)',
        }}>
          <button
            onClick={submitCart}
            disabled={busy}
            style={{
              width: '100%', padding: 14, borderRadius: 999, border: 'none',
              background: busy ? 'rgba(28,25,23,0.6)' : 'var(--primary, #1C1917)',
              color: 'var(--on-primary, #fff)', fontWeight: 700, fontSize: '0.95rem',
              cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background 0.2s',
            }}
          >
            {busy && <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'hb-spin 0.6s linear infinite' }} />}
            {busy ? 'در حال افزودن...' : 'افزودن به سبد خرید'}
          </button>
        </div>
      </div>
    </>
  );
}
